// src/components/dashboard/miDieta/DailyDietTabs.js
import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Divider,
  IconButton,
  Stack,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LocalDrinkIcon from "@mui/icons-material/LocalDrink";
import BreakfastDiningIcon from "@mui/icons-material/BreakfastDining";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import DinnerDiningIcon from "@mui/icons-material/DinnerDining";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import SwapIngredientModal from "./SwapIngredientModal";

/* ───────── utilidades pieza ↔ fracciones ───────── */

const FRAC_STEPS = [0, 0.25, 1 / 3, 0.5, 2 / 3, 0.75, 1];
const FRAC_WORD = {
  0.25: { mixed: "1 cuarto", solo: "1 cuarto" },
  0.33: { mixed: "1 tercio", solo: "1 tercio" },
  0.5: { mixed: "Media", solo: "Media" },
  0.66: { mixed: "2 tercios", solo: "2 tercios" },
  0.75: { mixed: "3 cuartos", solo: "3 cuartos" },
};

/* redondea la parte fraccionaria al paso más próximo (0 – .25 – ⅓ – …) */
const ajustarAFraccion = (cantidad = 0) => {
  const ent = Math.floor(cantidad);
  const resto = cantidad - ent;
  const closest = FRAC_STEPS.reduce(
    (p, c) => (Math.abs(c - resto) < Math.abs(p - resto) ? c : p),
    0
  );
  const tot = ent + (closest === 1 ? 1 : closest);
  return +tot.toFixed(2);
};

/* mapea unidades que usan fracciones y devuelve singular / plural */
const matchFraccionUnit = (u = "") => {
  const map = {
    pieza: { sing: "pieza", plur: "piezas" },
    piezas: { sing: "pieza", plur: "piezas" },
    pza: { sing: "pieza", plur: "piezas" },
    taza: { sing: "taza", plur: "tazas" },
    tazas: { sing: "taza", plur: "tazas" },
    rebanada: { sing: "rebanada", plur: "rebanadas" },
    rebanadas: { sing: "rebanada", plur: "rebanadas" },
    cucharada: { sing: "cucharada", plur: "cucharadas" },
    cucharadas: { sing: "cucharada", plur: "cucharadas" },
    cucharadita: { sing: "cucharadita", plur: "cucharaditas" },
    cucharaditas: { sing: "cucharadita", plur: "cucharaditas" },
  };
  return map[u] || null;
};

/* capitaliza primera letra */
const cap = (t = "") => t.charAt(0).toUpperCase() + t.slice(1);

/* ---------- formatCantidad (usa helpers de arriba) ---------- */
const formatCantidad = (ing = {}) => {
  const qtyRaw = Number(ing.cantidad) || 0;
  const unitRaw = String(ing.unidad || "").trim();
  const unitLow = unitRaw.toLowerCase();
  const match = matchFraccionUnit(unitLow);

  /* unidades con fracciones bonitas */
  if (match) {
    const qtyAdj = ajustarAFraccion(qtyRaw);
    const entero = Math.floor(qtyAdj);
    const resto = +(qtyAdj - entero).toFixed(2);
    const word = FRAC_WORD[resto] || null;

    if (entero === 0 && word)
      // solo fracción (<1)
      return `${word.solo} de ${match.sing}`;

    if (entero > 0 && resto && word)
      // entero + fracción
      return `${entero} ${cap(entero === 1 ? match.sing : match.plur)} y ${
        word.mixed
      }`;

    if (resto === 0)
      // entero exacto
      return `${entero} ${cap(entero === 1 ? match.sing : match.plur)}`;
  }

  /* otras unidades → 2 decimales normales */
  return `${qtyRaw.toFixed(2)} ${unitRaw}`.trim();
};

/* ────────────────────────── */
const dayNames = [
  "lunes",
  "martes",
  "miercoles",
  "jueves",
  "viernes",
  "sabado",
  "domingo",
];

const MEAL_SECTIONS = [
  {
    key: "desayuno",
    label: "Desayuno",
    icon: <BreakfastDiningIcon fontSize="small" />,
    accent: "#fff7ed",
  },
  {
    key: "comida",
    label: "Comida",
    icon: <LunchDiningIcon fontSize="small" />,
    accent: "#ecfccb",
  },
  {
    key: "cena",
    label: "Cena",
    icon: <DinnerDiningIcon fontSize="small" />,
    accent: "#e0f2fe",
  },
  {
    key: "colacion1",
    label: "Colación 1",
    icon: <FastfoodIcon fontSize="small" />,
    accent: "#fdf2f8",
  },
  {
    key: "colacion2",
    label: "Colación 2",
    icon: <FastfoodIcon fontSize="small" />,
    accent: "#fdf2f8",
  },
  {
    key: "colacion3",
    label: "Colación 3",
    icon: <FastfoodIcon fontSize="small" />,
    accent: "#fef9c3",
  },
];

const toArray = (value) =>
  Array.isArray(value) ? value : value ? Object.values(value) : [];
const countMealIngredients = (mealData) => {
  if (!mealData?.contenido?.length) return 0;
  return mealData.contenido.reduce((total, entry) => {
    if (entry.platillo) {
      return total + toArray(entry.platillo.ingredientes).length;
    }
    if (entry.comida) {
      const fromComida = toArray(entry.comida.ingredientes).length;
      const fromPlatillos = toArray(entry.comida.platillos).reduce(
        (sum, platillo) => sum + toArray(platillo.ingredientes).length,
        0
      );
      return total + fromComida + fromPlatillos;
    }
    if (entry.ingrediente) return total + 1;
    if (entry.suplemento) return total + 1;
    return total;
  }, 0);
};

export default function DailyDietTabs({
  dietasAsignadas,
  selectedDay,
  setSelectedDay,
  color3,
  ingredientes,
}) {
  const [swapOpen, setSwapOpen] = useState(false);
  const [currentCat, setCurrentCat] = useState("");
  const [swapTarget, setSwapTarget] = useState(null);

  const currentIndex = dayNames.indexOf(selectedDay);
  const handleChange = (_, newVal) => setSelectedDay(dayNames[newVal]);

  if (!dietasAsignadas?.length)
    return <Typography>No hay dietas asignadas</Typography>;

  const dieta = dietasAsignadas[0];
  const daysObj = dieta.dias?.[0] || {};
  const currentDayData = daysObj[selectedDay];
  const alimentosDia = currentDayData?.alimentosDia || [];

  const handleOpenSwap = (ing) => {
    setCurrentCat(ing.categoria);
    setSwapTarget(ing);
    setSwapOpen(true);
  };
  const handleCloseSwap = () => {
    setSwapOpen(false);
    setSwapTarget(null);
  };
  const handleSelectIngredient = () => handleCloseSwap();

  /* ───────── helpers de render ───────── */
  const renderIngredientesList = (lista) => {
    const items = Array.isArray(lista)
      ? lista
      : lista
      ? Object.values(lista)
      : [];
    if (!items.length) return null;
    return (
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        {items.map((ing, idx) => (
          <Stack
            key={idx}
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <IconButton size="small" onClick={() => handleOpenSwap(ing)}>
                <SwapHorizIcon fontSize="inherit" />
              </IconButton>
              <Typography variant="body2">
                {formatCantidad(ing)} de <b>{ing.nombre}</b>
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>
    );
  };

  const renderPlatillo = (p) => (
    <Box sx={{ mb: 1.25 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        {p.nombre}
      </Typography>
      {renderIngredientesList(p.ingredientes)}
    </Box>
  );

  const renderIngrediente = (ing) => (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 0.25 }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center">
        <IconButton size="small" onClick={() => handleOpenSwap(ing)}>
          <SwapHorizIcon fontSize="inherit" />
        </IconButton>
        <Typography variant="body2">
          {formatCantidad(ing)} de <b>{ing.nombre}</b>
        </Typography>
      </Stack>
    </Stack>
  );

  const renderSuplemento = (sup) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.25 }}>
      <LocalDrinkIcon fontSize="small" sx={{ color: color3 }} />
      <Typography variant="body2">
        <b>Suplemento:</b> {formatCantidad(sup)} de <b>{sup.nombre}</b>
      </Typography>
    </Stack>
  );

  const renderMeal = (mealData, mealName) => {
    if (!mealData?.contenido?.length)
      return (
        <Typography variant="body2" color="text.secondary">
          No hay datos para {mealName}
        </Typography>
      );
    return (
      <Stack spacing={0.75}>
        {mealData.contenido.map((cont, i) => {
          if (cont.platillo)
            return (
              <React.Fragment key={i}>
                {renderPlatillo(cont.platillo)}
              </React.Fragment>
            );
          if (cont.ingrediente)
            return (
              <React.Fragment key={i}>
                {renderIngrediente(cont.ingrediente)}
              </React.Fragment>
            );
          if (cont.suplemento)
            return (
              <React.Fragment key={i}>
                {renderSuplemento(cont.suplemento)}
              </React.Fragment>
            );
          return (
            <Typography key={i} variant="body2" color="text.secondary">
              (Dato no definido)
            </Typography>
          );
        })}
      </Stack>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={currentIndex}
        onChange={handleChange}
        centered
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ style: { backgroundColor: color3 } }}
        sx={{
          mb: 2,
          "& .MuiTab-root": {
            textTransform: "capitalize",
            fontWeight: 600,
            minHeight: 36,
          },
          "& .MuiTab-root.Mui-selected": {
            color: color3,
          },
        }}
      >
        {dayNames.map((d) => (
          <Tab key={d} label={d.charAt(0).toUpperCase() + d.slice(1)} />
        ))}
      </Tabs>

      {currentDayData ? (
        alimentosDia.map((al, idx) => (
          <Stack key={idx} spacing={1.5} sx={{ mb: 2 }}>
            {MEAL_SECTIONS.map(({ key, label, icon, accent }) => {
              const ingredientCount = countMealIngredients(al[key]);
              const ingredientLabel = `${ingredientCount} ${
                ingredientCount === 1 ? "ingrediente" : "ingredientes"
              }`;
              return (
                <Box
                  key={`${idx}-${key}`}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid rgba(15,23,42,0.08)",
                    background: accent,
                    p: 1.5,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      {icon}
                      <Typography variant="subtitle2" fontWeight={700}>
                        {label}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      {ingredientLabel}
                    </Typography>
                  </Stack>
                  <Divider sx={{ my: 1 }} />
                  {renderMeal(al[key], label)}
                </Box>
              );
            })}
          </Stack>
        ))
      ) : (
        <Typography>No hay datos para {selectedDay}</Typography>
      )}

      <SwapIngredientModal
        open={swapOpen}
        onClose={handleCloseSwap}
        ingredientes={ingredientes}
        categoryId={currentCat}
        onSelect={handleSelectIngredient}
        swapTarget={swapTarget}
      />
    </Box>
  );
}
